import { Form, Input } from 'antd';
import React, { FunctionComponent, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Color } from '../config/Color';
import { AppSelector, useAppStore } from '../store/Store';
import { appOverride } from '../util/AppUtils';
import { TextInput, textInputStyles } from './styles/TextInput';
import { BodyXXS } from './styles/Typography';

const formContent = {
  name: 'First name',
  lastName: 'Last name',
  email: 'Email',
  confirmEmail: 'Confirm email',
  password: 'Password (min 6 characters)',
};

type SignUpFormProps = {
  /** Optional note rendered directly below the password field, inside the form's own spacing. */
  callout?: ReactNode;
};

export const SignUpForm: FunctionComponent<SignUpFormProps> = (props) => {
  const user = useAppStore(AppSelector.userState);

  return (
    <>
      <Form.Item name="name">
        <StyledTextInput
          size="large"
          autoComplete="off"
          type="text"
          placeholder={formContent.name}
          aria-label={formContent.name}
        />
      </Form.Item>
      <Form.Item name="lastName">
        <StyledTextInput
          size="large"
          autoComplete="off"
          type="text"
          placeholder={formContent.lastName}
          aria-label={formContent.lastName}
        />
      </Form.Item>
      <Form.Item name="email">
        <StyledTextInput
          size="large"
          autoComplete="email"
          type="email"
          placeholder={formContent.email}
          aria-label={formContent.email}
        />
      </Form.Item>
      <Form.Item name="confirmEmail">
        <StyledTextInput
          size="large"
          autoComplete="email"
          type="email"
          data-test="signupConfirmEmail"
          placeholder={formContent.confirmEmail}
          aria-label={formContent.confirmEmail}
        />
      </Form.Item>
      <Form.Item
        name="password"
        validateTrigger="onBlur"
        rules={[{ min: 6, message: 'Password must be minimum of 6 characters.' }]}
      >
        <StyledPasswordInput
          size="large"
          autoComplete={'new-password'}
          type="password"
          placeholder={formContent.password}
          aria-label={formContent.password}
        />
      </Form.Item>
      {props.callout}
      <ErrorBody data-test="authErrors" style={{ textAlign: 'center' }}>
        {user.error?.message}
      </ErrorBody>
    </>
  );
};

export const LoginForm: FunctionComponent<{ onForgotPassword?: () => void }> = ({
  onForgotPassword,
}) => {
  const user = useAppStore(AppSelector.userState);
  return (
    <>
      <Form.Item name="email" required>
        <StyledTextInput
          size="large"
          autoComplete="email"
          type="email"
          placeholder={formContent.email}
          aria-label={formContent.email}
        />
      </Form.Item>
      <Form.Item name="password" required>
        <StyledPasswordInput
          size="large"
          autoComplete={'current-password'}
          type="password"
          placeholder={formContent.password}
          aria-label={formContent.password}
        />
      </Form.Item>
      {onForgotPassword && (
        <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '12px' }}>
          <ForgotPasswordLink onClick={onForgotPassword}>Forgot password?</ForgotPasswordLink>
        </div>
      )}
      <ErrorBody data-test="authErrors" style={{ textAlign: 'center' }}>
        {user.error?.message}
      </ErrorBody>
    </>
  );
};

export const ForgotPasswordForm: FunctionComponent = () => {
  const user = useAppStore(AppSelector.userState);
  return (
    <>
      <Form.Item name="email" required>
        <StyledTextInput
          size="large"
          autoComplete="email"
          type="email"
          placeholder={formContent.email}
          aria-label={formContent.email}
        />
      </Form.Item>
      <ErrorBody data-test="authErrors" style={{ textAlign: 'center' }}>
        {user.error?.message}
      </ErrorBody>
    </>
  );
};

const ForgotPasswordLink = styled(BodyXXS)`
  cursor: pointer;
  text-decoration: underline;
  color: ${Color.content.default};
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
`;

const styledTextInputStyle = css`
  ${textInputStyles}
  &, &:focus, &:hover {
    text-align: left;
    border-width: 1px;
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

const StyledTextInput = styled(TextInput)`
  ${styledTextInputStyle}
`;

const StyledPasswordInput = styled(Input.Password)`
  ${styledTextInputStyle}

  input {
    background-color: transparent;
    color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};

    &::placeholder {
      color: ${appOverride({ nerva: '#f5f5f5', default: Color.content.default })};
      opacity: 0.25;
    }
  }
  &:hover {
    border-color: ${Color.flow.default} !important;
  }

  /* Password visibility icon styling for Nerva */
  .ant-input-password-icon {
    color: ${appOverride({ nerva: '#f5f5f5', default: 'inherit' })};
    opacity: ${appOverride({ nerva: '0.25', default: '1' })};

    &:hover {
      color: ${appOverride({ nerva: '#f5f5f5', default: 'inherit' })};
      opacity: ${appOverride({ nerva: '0.25', default: '1' })};
    }
  }

  /* When input has value, make icon fully opaque white */
  &.ant-input-affix-wrapper-focused .ant-input-password-icon,
  input:not(:placeholder-shown) ~ .ant-input-suffix .ant-input-password-icon {
    color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};
    opacity: 1;
  }
`;

const ErrorBody = styled(BodyXXS)`
  color: ${Color.state.error};
`;
